import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MediaHelperService} from '../../core/media-helper.service';
import {SuperObjectModel} from '../../core/definitions/super-object-model';
import {LoggerService} from '../../core/logger.service';
import {RegionParams} from "wavesurfer.js/plugins/regions";
import {AudioWaveformPlayerComponent} from "../../media-players/audio-waveform-player/audio-waveform-player.component";
import {DmsVideoPlayerComponent} from "../dms-video-player/dms-video-player.component";

@Component({
  selector: 'app-active-media',
  templateUrl: './active-media.component.html',
  styleUrls: ['./active-media.component.scss'],
  standalone: false
})
export class ActiveMediaComponent implements OnChanges, AfterViewInit, OnDestroy {
  // Injections
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly mediaHelper: MediaHelperService = inject(MediaHelperService);

  dragItem: any;
  imageSize = {width: 0, height: 0};
  naturalImageSize = {width: 0, height: 0};
  imageLoaded = 0;
  playMedia = false;
  uploadStatus: string;
  validThumbnailSrc = true;
  imageUrl: string;
  segmentRegions: RegionParams[] = [];
  activeRegionId: string | null = null;

  private waitForProgressId: any;
  private killMe = false;
  private audioPlayer?: AudioWaveformPlayerComponent;

  readonly dmsApiUrl = this.mediaHelper.getDmsApiUrl();

  @Input() activeMedia: SuperObjectModel;
  @Input() selectedSegmentId: string | null;
  @Input() zoomValue: number;
  @Input() container: HTMLDivElement;
  @Input() fullscreen: boolean;

  @ViewChild(DmsVideoPlayerComponent) private videoPlayer?: DmsVideoPlayerComponent;

  @ViewChild(AudioWaveformPlayerComponent)
  set audioPlayerComponent(player: AudioWaveformPlayerComponent | undefined) {
    this.audioPlayer = player;
    if (player && this.activeRegionId) {
      player.goToRegion(this.activeRegionId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const segmentChanged = changes.hasOwnProperty('selectedSegmentId');
    const mediaChanged = changes.hasOwnProperty('activeMedia') &&
      changes.activeMedia.previousValue !== changes.activeMedia.currentValue &&
      changes.activeMedia.currentValue !== undefined;

    if (mediaChanged || changes.hasOwnProperty('fullscreen')) {
      this.setMediaInfo().then();
    }

    if (mediaChanged) {
      this.setSegmentRegions();
      this.setActiveImageSize();
      this.resizeActiveImage(this.zoomValue);
    } else if (changes.hasOwnProperty('zoomValue') &&
      changes.zoomValue.previousValue !== changes.zoomValue.currentValue &&
      !!changes.zoomValue.currentValue) {
      this.resizeActiveImage(changes.zoomValue.currentValue);
    }

    if (mediaChanged || segmentChanged) {
      this.setActiveRegion();
    }
  }

  onPlayMedia() {
    this.playMedia = true;
  }

  get objectType() {
    return this.activeMedia?.object_type;
  }

  get isPlayable() {
    return !!this.activeMedia?.$$mediaPlaybackUrls;
  }

  // Audio, and video with recording segments, are played in the waveform player so the segments can be shown
  get showWaveformPlayer() {
    return this.objectType === 'Audio'
      || (this.objectType === 'Video' && !!this.activeMedia?.$$mediaSegments?.length);
  }

  get waveformPlaybackUrl() {
    return this.mediaHelper.getWaveformPlaybackUrl(this.activeMedia);
  }

  ngAfterViewInit() {
    this.dragItem = this.getDragItem();
    this.imageLoaded = 1;
  }

  setDefaultImg() {
    this.validThumbnailSrc = false;
  }

  getDragItem() {
    return this.activeMedia === undefined ? this.elementRef.nativeElement.querySelector('#mediaItem-') :
      this.elementRef.nativeElement.querySelector('#mediaItem-' + this.activeMedia.artifact_id);
  }

  setActiveImageSize() {
    setTimeout(() => {
      this.dragItem = this.getDragItem();
    }, 100);
    setTimeout(() => {
      if (this.dragItem) {
        this.imageSize = this.mediaHelper.setNewImageSize(
          this.container, this.dragItem, this.dragItem.naturalWidth, this.dragItem.naturalHeight);
      } else {
        this.logger.warn('Drag item object not defined');
      }
    }, 200);
  }

  resizeActiveImage(value: number) {
    if (this.imageLoaded === 1 && this.dragItem) {
      this.zoomValue = value;

      if (this.imageSize.width === 0 && this.imageSize.height === 0) {
        this.imageSize.width = this.dragItem.width;
        this.imageSize.height = this.dragItem.height;
      }

      this.naturalImageSize = {
        width: this.dragItem.naturalWidth,
        height: this.dragItem.naturalHeight
      };

      this.mediaHelper.resizeImage(
        this.container,
        this.zoomValue,
        this.dragItem,
        this.naturalImageSize,
        this.imageSize, false);

      if (this.dragItem.width > this.container.offsetWidth ||
        this.dragItem.height > this.container.offsetHeight) {
        this.mediaHelper.imgDrag(
          this.container,
          this.imageLoaded,
          this.dragItem,
          this.imageSize,
          this.zoomValue);
      } else {
        this.stopCallBack();
      }
    }

  }

  refitActiveMedia(): void {
    this.dragItem = this.getDragItem();
    if (!this.dragItem) {
      return;
    }
    this.imageSize = this.mediaHelper.setNewImageSize(
      this.container, this.dragItem, this.dragItem.naturalWidth, this.dragItem.naturalHeight);
    this.resizeActiveImage(this.zoomValue);
  }

  goToStart(): void {
    this.audioPlayer?.goToStart();
  }

  togglePlayback(): void {
    if (this.audioPlayer) {
      this.audioPlayer.togglePlay();
    } else if (this.videoPlayer) {
      this.videoPlayer.togglePlay();
    } else if (this.isPlayable && this.objectType === 'Video') {
      // The video player is not opened yet; it autoplays once shown
      this.onPlayMedia();
    }
  }

  toggleSegmentPlayback(segmentId: string): void {
    if (segmentId) {
      this.audioPlayer?.toggleRegionPlayback(segmentId);
    }
  }

  stopCallBack(): void {
    this.mediaHelper.stopCallBack();
  }

  ngOnDestroy() {
    this.stopCallBack();
    this.imageLoaded = 0;
    this.killMe = true;
    if (this.waitForProgressId) {
      clearTimeout(this.waitForProgressId);
    }
  }

  private async setMediaInfo() {
    this.playMedia = false;
    this.uploadStatus = '';
    if (!this.activeMedia) {
      this.imageUrl = '';
      this.validThumbnailSrc = false;
      return;
    }
    if (this.fullscreen) {
      this.imageUrl = this.activeMedia.$$maxImageUrl;
    } else {
      this.imageUrl = this.activeMedia.$$largeImageUrl;
    }
    this.validThumbnailSrc = !!this.imageUrl;

    const mediaObjType = this.activeMedia.object_type;
    if (mediaObjType === 'Video' || mediaObjType === 'Audio') {
      if (!this.activeMedia.$$mediaPlaybackUrls) {
        this.uploadStatus = await this.mediaHelper.getMediaUploadStatus(this.activeMedia);
        if (this.uploadStatus === 'done') {
          this.activeMedia.$$mediaPlaybackUrls = await this.mediaHelper.getMediaPlaybackUrls(this.activeMedia);
        } else if (this.uploadStatus === 'convert' || this.uploadStatus === 'converting') {
          this.waitForProgress();
        }
      } else {
        this.uploadStatus = 'done';
      }
    }

    // Load the waveform player automatically, so segments can be selected right away
    this.playMedia = this.showWaveformPlayer && !!this.activeMedia.$$mediaPlaybackUrls;
  }

  private setSegmentRegions() {
    const segments = this.activeMedia?.$$mediaSegments || [];
    this.segmentRegions = segments.map(segment => ({
      id: segment.segmentId,
      content: segment.remarks || segment.name,
      start: segment.start / 1000,
      end: segment.end / 1000
    }));
  }

  private setActiveRegion(): void {
    const segmentId = this.selectedSegmentId || null;
    const known = !!segmentId && this.segmentRegions.some(region => region.id === segmentId);
    this.activeRegionId = known ? segmentId : null;
    if (this.activeRegionId) {
      this.audioPlayer?.goToRegion(this.activeRegionId);
    }
  }

  private waitForProgress() {
    if (this.waitForProgressId) {
      clearTimeout(this.waitForProgressId);
    }
    this.waitForProgressId = setTimeout(() => {
      this.mediaHelper.getMediaUploadProgress(this.activeMedia).then(progress => {
        this.activeMedia.$$uploadProgress = progress;
        if (progress !== 100 && !this.killMe) {
          this.waitForProgress();
        }
      });
    }, 1000);
  }

}
